import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  StopOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import type { RcFile } from 'antd/es/upload';
import { useEffect, useState } from 'react';
import {
  DEFAULT_DOCUMENT_PREFIX,
  DocumentKind,
  DOCUMENT_KIND_LABEL,
  OfficeKind,
  PERMISSIONS,
  WaybillTaskLayout,
} from '@tms/shared';

import { CardTitle } from '@/components/EntityId';
import { GOOGLE_MAPS_KEY } from '@/lib/googleMaps';
import { api } from '@/api/client';
import { errorNotice } from '@/api/errorNotice';
import { useApiMutation, useAuthedImage } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';

import { OfficeTerritoryPicker, type Territory } from './OfficeTerritoryPicker';

interface OrganizationRow {
  id: number;
  code: string;
  nameRu: string;
  isActive: boolean;
  taskLayout: string;
  taskAddressALocations: boolean;
}

interface OfficeRow {
  id: number;
  code: string;
  organizationId: number;
  organization: { id: number; code: string; nameRu: string };
  kind: string;
  parentId: number | null;
  nameRu: string;
  nameUz: string;
  nameEn: string;
  iataCode: string | null;
  icaoCode: string | null;
  city: string | null;
  timezone: string;
  logoKey: string | null;
  isActive: boolean;
  createdAt: string;
  /** null — как у организации */
  taskLayout: string | null;
  taskAddressALocations: boolean | null;
  violationLimit: number;
  violationPeriodMonths: number;
}

const KIND_LABEL: Record<string, string> = {
  HEADQUARTERS: 'Головной офис',
  AIRPORT: 'Аэропорт',
  BRANCH: 'Филиал',
};

/** Значение выбора «как у организации» — на сервер уходит как null. */
const INHERIT = 'INHERIT';

export const TASK_LAYOUT_LABEL: Record<string, string> = {
  FLIGHT: 'Рейс / Борт / Стоянка (авиация)',
  ADDRESS: 'Адрес А / Адрес Б (универсальная)',
  REGION_DISTRICT: 'Регион / Район (административное деление)',
};

const MONTHS = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
];

const MAX_LOGO_BYTES = 10 * 1024 * 1024;

/**
 * Логотип в таблице: он же кнопка загрузки и замены. Общий для офисов и
 * организаций — отличается только адрес ресурса.
 */
export function OfficeLogoCell({
  office,
  manage,
  resource = 'offices',
}: {
  office: { id: number; code: string; logoKey: string | null };
  manage: boolean;
  resource?: 'offices' | 'organizations';
}) {
  const { t } = useTranslation();

  const { message } = App.useApp();
  const queryClient = useQueryClient();
  // Ключ входит в URL, поэтому после замены картинка перезапрашивается сама,
  // а не берётся из кэша браузера как старая.
  const src = useAuthedImage(office.logoKey ? `/${resource}/${office.id}/logo` : null);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['offices-admin'] });
    await queryClient.invalidateQueries({ queryKey: ['offices'] });
    await queryClient.invalidateQueries({ queryKey: ['organizations'] });
  };

  const upload = async (file: RcFile): Promise<void> => {
    if (file.size > MAX_LOGO_BYTES) {
      void message.error(t('Файл больше 10 МБ'));
      return;
    }
    const form = new FormData();
    form.append('file', file);
    try {
      await api.post(`/${resource}/${office.id}/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      void message.success(t('Логотип обновлён'));
      await refresh();
    } catch (error) {
      void message.error(errorNotice(error));
    }
  };

  const remove = async (): Promise<void> => {
    try {
      await api.delete(`/${resource}/${office.id}/logo`);
      void message.success(t('Логотип удалён'));
      await refresh();
    } catch (error) {
      void message.error(errorNotice(error));
    }
  };

  // Высота задана, ширина — по пропорциям файла: логотип показывается таким,
  // каким загружен, а не вписанным в квадрат с пустыми полями.
  const preview = src ? (
    <img
      src={src}
      alt=""
      style={{ height: 28, width: 'auto', maxWidth: 96, objectFit: 'contain', display: 'block' }}
    />
  ) : (
    <div
      style={{
        height: 28,
        display: 'flex',
        alignItems: 'center',
        padding: '0 8px',
        borderRadius: 4,
        background: '#f0f0f0',
        color: '#8c8c8c',
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {office.code.slice(0, 3)}
    </div>
  );

  if (!manage) return preview;

  return (
    <Space size={4}>
      {preview}
      <Upload
        accept="image/jpeg,image/png,image/webp"
        showUploadList={false}
        beforeUpload={(file) => {
          void upload(file);
          return false;
        }}
      >
        <Tooltip title={office.logoKey ? t('Заменить логотип') : t('Загрузить логотип')}>
          <Button type="text" size="small" icon={<UploadOutlined />} />
        </Tooltip>
      </Upload>
      {office.logoKey && (
        <Popconfirm title={t("Удалить логотип?")} okText={t("Удалить")} cancelText={t("Отмена")} onConfirm={remove}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )}
    </Space>
  );
}

/**
 * Офисы одной организации — раскрывающаяся часть строки на вкладке
 * «Организации» платформы: организация и её офисы показаны иерархией,
 * а не двумя отдельными таблицами.
 *
 * Все офисы приходят одним запросом (общий кэш ['offices-admin']) и
 * делятся по организациям уже здесь: раскрытие строки не порождает
 * отдельного запроса на каждую организацию.
 */
export function OfficesPanel({ organizationId }: { organizationId: number }) {
  const { t } = useTranslation();

  const { can, refreshProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<OfficeRow | null>(null);
  const [form] = Form.useForm();
  const [territoryOpen, setTerritoryOpen] = useState(false);
  // Территория из формы на момент открытия окна выбора.
  const [territoryStart, setTerritoryStart] = useState<Territory | null>(null);

  const manage = can(PERMISSIONS.PLATFORM_MANAGE);

  const organizations = useQuery({
    queryKey: ['organizations'],
    enabled: manage,
    queryFn: async () => (await api.get<OrganizationRow[]>('/organizations')).data,
  });
  const watchedOrganizationId = Form.useWatch<number | undefined>('organizationId', form);
  const officeOrganization = organizations.data?.find((org) => org.id === watchedOrganizationId);
  const inheritLabel = officeOrganization
    ? `${t('Как у организации')} (${t(TASK_LAYOUT_LABEL[officeOrganization.taskLayout] ?? officeOrganization.taskLayout)})`
    : t('Как у организации');

  // Отключённые запрашиваются намеренно: иначе офис, однажды отключённый,
  // пропал бы и отсюда, и включить его обратно было бы нечем.
  const offices = useQuery({
    queryKey: ['offices-admin'],
    queryFn: async () =>
      (await api.get<OfficeRow[]>('/offices', { params: { includeInactive: true } })).data,
  });

  const toggleActive = useApiMutation(
    async (office: OfficeRow) =>
      (await api.patch(`/offices/${office.id}`, { isActive: !office.isActive })).data,
    {
      successMessage: t("Состояние офиса изменено"),
      invalidate: [['offices-admin'], ['offices']],
    },
  );

  // Полная карточка нужна ради зимней надбавки: в списке её нет.
  const detail = useQuery({
    queryKey: ['office-detail', editing?.id],
    enabled: open && editing !== null,
    queryFn: async () => (await api.get(`/offices/${editing!.id}`)).data as Record<string, unknown>,
  });

  useEffect(() => {
    if (!open) return;
    if (!editing) {
      form.resetFields();
      form.setFieldsValue({
        organizationId,
        kind: OfficeKind.AIRPORT,
        timezone: 'Asia/Tashkent',
        mapRadiusKm: 6,
        winterSurchargePct: 8,
        winterFromMonth: 11,
        winterToMonth: 3,
        taskLayout: INHERIT,
        taskAddressALocations: false,
      });
      return;
    }
    if (detail.data) {
      form.setFieldsValue({
        ...editing,
        winterSurchargePct: Number(detail.data.winterSurchargePct ?? 0),
        winterFromMonth: detail.data.winterFromMonth,
        winterToMonth: detail.data.winterToMonth,
        address: detail.data.address,
        phone: detail.data.phone,
        latitude: detail.data.latitude != null ? Number(detail.data.latitude) : null,
        longitude: detail.data.longitude != null ? Number(detail.data.longitude) : null,
        mapRadiusKm: detail.data.mapRadiusKm ?? 6,
        isActive: detail.data.isActive,
        taskLayout: detail.data.taskLayout ?? INHERIT,
        taskAddressALocations: detail.data.taskAddressALocations ?? false,
        documentNumberPrefixes: detail.data.documentNumberPrefixes ?? {},
        violationLimit: detail.data.violationLimit,
        violationPeriodMonths: detail.data.violationPeriodMonths,
      });
    }
  }, [open, editing, detail.data, form, organizationId]);

  const save = useApiMutation(
    async (formValues: Record<string, unknown>) => {
      // «Как у организации» хранится как null — и раскладка, и список локаций.
      const inherits = formValues.taskLayout === INHERIT;
      const prefixes = (formValues.documentNumberPrefixes ?? {}) as Record<string, string>;
      const values: Record<string, unknown> = {
        ...formValues,
        taskLayout: inherits ? null : formValues.taskLayout,
        taskAddressALocations: inherits
          ? null
          : formValues.taskLayout === WaybillTaskLayout.ADDRESS
            ? Boolean(formValues.taskAddressALocations)
            : false,
        // Пустая строка — «как у платформы по умолчанию», лишним ключом не отправляется.
        documentNumberPrefixes: Object.fromEntries(
          Object.entries(prefixes).filter(([, value]) => value?.trim()),
        ),
      };
      if (editing) {
        // Поля "Код" и "Тип" зарегистрированы в форме (и потому попадают в
        // values) даже будучи disabled — antd не исключает их из результата
        // validateFields(). UpdateOfficeDto их не объявляет, а ValidationPipe
        // настроен с forbidNonWhitelisted: любое лишнее свойство в теле
        // запроса — это 400, а не молчаливый игнор.
        // То же для «Организации» и «Головного офиса»: они задаются при создании.
        const {
          code: _code,
          kind: _kind,
          organizationId: _organizationId,
          parentId: _parentId,
          ...patchValues
        } = values;
        return (await api.patch(`/offices/${editing.id}`, patchValues)).data;
      }
      return (await api.post('/offices', values)).data;
    },
    {
      successMessage: editing ? t('Офис обновлён') : t('Офис добавлен'),
      // Счётчик офисов в строке организации тоже меняется.
      invalidate: [['offices-admin'], ['offices'], ['organizations']],
    },
  );

  // Головной офис — первым, остальные по названию: так строки читаются
  // сверху вниз как структура организации.
  const rows = (offices.data ?? [])
    .filter((office) => office.organizationId === organizationId)
    .sort(
      (a, b) =>
        Number(b.kind === 'HEADQUARTERS') - Number(a.kind === 'HEADQUARTERS') ||
        a.nameRu.localeCompare(b.nameRu, 'ru'),
    );

  return (
    <div className="tms-org-offices">
      <Table<OfficeRow>
        rowKey="id"
        size="small"
        loading={offices.isLoading}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: t('В организации пока нет офисов') }}
        columns={[
          {
            title: t("Логотип"),
            key: 'logo',
            width: 130,
            render: (_: unknown, row: OfficeRow) => (
              <OfficeLogoCell office={row} manage={manage} />
            ),
          },
          {
            title: t("Код"),
            dataIndex: 'code',
            width: 90,
            // Код входит в номера документов: PL-TAS-2026-000123.
            render: (code: string) => (
              <Tooltip title={t('Код офиса входит в номера документов и после создания не меняется')}>
                <span className="tms-cell-key">{code}</span>
              </Tooltip>
            ),
          },
          { title: t("Наименование"), dataIndex: 'nameRu' },
          {
            title: t("Тип"),
            dataIndex: 'kind',
            width: 150,
            render: (value: string) => (
              <Tag color={value === 'HEADQUARTERS' ? 'purple' : 'blue'}>
                {t(KIND_LABEL[value] ?? value)}
              </Tag>
            ),
          },
          { title: 'IATA', dataIndex: 'iataCode', width: 70 },
          { title: 'ICAO', dataIndex: 'icaoCode', width: 70 },
          { title: t("Город"), dataIndex: 'city', width: 140 },
          { title: t("Часовой пояс"), dataIndex: 'timezone', width: 150 },
          {
            title: t("Состояние"),
            dataIndex: 'isActive',
            width: 120,
            render: (isActive: boolean) =>
              isActive ? (
                <Tag color="green">{t('В работе')}</Tag>
              ) : (
                <Tag color="default">{t('Отключён')}</Tag>
              ),
          },
          ...(manage
            ? [
                {
                  title: '',
                  width: 100,
                  render: (_: unknown, row: OfficeRow) => (
                    <Space size={0}>
                      <Tooltip title={t("Изменить")}>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditing(row);
                            setOpen(true);
                          }}
                        />
                      </Tooltip>
                      <Popconfirm
                        title={row.isActive ? t('Отключить офис?') : t('Включить офис?')}
                        description={
                          row.isActive
                            ? t('Офис исчезнет из переключателя и списков. Данные и история сохранятся, включить можно в любой момент. Сотрудники этого офиса войти не смогут.')
                            : t('Офис вернётся в переключатель и станет доступен сотрудникам.')
                        }
                        okText={row.isActive ? t('Отключить') : t('Включить')}
                        cancelText={t("Отмена")}
                        onConfirm={() =>
                          toggleActive.mutate(row, { onSuccess: () => void refreshProfile() })
                        }
                      >
                        <Tooltip title={row.isActive ? t('Отключить') : t('Включить')}>
                          <Button
                            type="text"
                            danger={row.isActive}
                            icon={row.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                          />
                        </Tooltip>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />

      {manage && (
        <Button
          size="small"
          icon={<PlusOutlined />}
          style={{ marginTop: 8 }}
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          {t('Добавить офис')}
        </Button>
      )}

      <Modal
        open={open}
        width={760}
        title={
          <CardTitle
            title={editing ? `${t('Офис')} ${editing.code}` : t('Новый офис')}
            id={editing?.id}
          />
        }
        okText={t("Сохранить")}
        cancelText={t("Отмена")}
        confirmLoading={save.isPending}
        onCancel={() => setOpen(false)}
        onOk={() => {
          void form.validateFields().then((values) => {
            save.mutate(values, {
              onSuccess: () => {
                setOpen(false);
                // Список офисов в шапке берётся из профиля — обновляем его.
                void refreshProfile();
              },
            });
          });
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="organizationId"
            label={t("Организация")}
            tooltip={t("Клиент платформы, которому принадлежит офис. После создания не меняется.")}
            rules={[{ required: true, message: t("Обязательное поле") }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              disabled={Boolean(editing)}
              placeholder={t("Выберите организацию")}
              options={(organizations.data ?? [])
                .filter((org) => org.isActive || org.id === editing?.organizationId)
                .map((org) => ({ value: org.id, label: org.nameRu }))}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="code"
                label={t("Код")}
                tooltip={t("2–8 заглавных латинских букв. После создания не меняется.")}
                rules={[
                  { required: true, message: t("Обязательное поле") },
                  { pattern: /^[A-Z]{2,8}$/, message: t("2–8 заглавных латинских букв") },
                ]}
              >
                <Input disabled={Boolean(editing)} placeholder="JIZ" />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item name="kind" label={t("Тип")} rules={[{ required: true }]}>
                <Select
                  disabled={Boolean(editing)}
                  options={Object.values(OfficeKind).map((value) => ({
                    value,
                    label: t(KIND_LABEL[value] ?? value),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item name="parentId" label={t("Головной офис")}>
                <Select
                  allowClear
                  disabled={Boolean(editing)}
                  options={(offices.data ?? [])
                    .filter(
                      (office) =>
                        office.kind === 'HEADQUARTERS' &&
                        office.organizationId === watchedOrganizationId,
                    )
                    .map((office) => ({ value: office.id, label: office.nameRu }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="nameRu" label={t("Название (рус)")} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="nameUz" label={t("Название (узб)")} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="nameEn" label={t("Название (англ)")} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={5}>
              <Form.Item name="iataCode" label="IATA">
                <Input maxLength={3} placeholder="JIZ" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="icaoCode" label="ICAO">
                <Input maxLength={4} placeholder="UTSJ" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name="city" label={t("Город")}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name="timezone" label={t("Часовой пояс")}>
                <Select
                  options={[
                    { value: 'Asia/Tashkent', label: 'Asia/Tashkent' },
                    { value: 'Asia/Samarkand', label: 'Asia/Samarkand' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          {/*
            Территория офиса — центр и радиус карты телеметрии. Удобнее всего
            выбрать на карте: поиск по адресу, щелчок по центру, радиус в км.
            Поля оставлены и для ручного ввода — например, координат из документа.
          */}
          <Typography.Text strong>{t('Территория офиса на карте')}</Typography.Text>
          <Row gutter={16} align="bottom" style={{ marginTop: 8 }}>
            <Col span={6}>
              <Form.Item name="latitude" label={t('Широта')}>
                <InputNumber
                  min={-90}
                  max={90}
                  step={0.0001}
                  style={{ width: '100%' }}
                  placeholder="41.2995"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="longitude" label={t('Долгота')}>
                <InputNumber
                  min={-180}
                  max={180}
                  step={0.0001}
                  style={{ width: '100%' }}
                  placeholder="69.2401"
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="mapRadiusKm"
                label={t('Радиус, км')}
                rules={[{ required: true, message: t('Обязательное поле') }]}
                tooltip={t('Карта телеметрии не уходит дальше этого расстояния от центра')}
              >
                <InputNumber min={1} max={200} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item>
                <Tooltip
                  title={
                    GOOGLE_MAPS_KEY
                      ? undefined
                      : t('Выбор на карте работает с картой Google — задайте её ключ в сборке сайта')
                  }
                >
                  <Button
                    block
                    icon={<EnvironmentOutlined />}
                    disabled={!GOOGLE_MAPS_KEY}
                    onClick={() => {
                      const lat = form.getFieldValue('latitude') as number | null | undefined;
                      const lon = form.getFieldValue('longitude') as number | null | undefined;
                      const radiusKm = (form.getFieldValue('mapRadiusKm') as number | undefined) ?? 6;
                      setTerritoryStart(lat != null && lon != null ? { lat, lon, radiusKm } : null);
                      setTerritoryOpen(true);
                    }}
                  >
                    {t('Указать на карте')}
                  </Button>
                </Tooltip>
              </Form.Item>
            </Col>
          </Row>

          <Typography.Text strong>{t('Зимняя надбавка к норме расхода')}</Typography.Text>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={8}>
              <Form.Item
                name="winterSurchargePct"
                label={t("Надбавка, %")}
                tooltip={t("Применяется ко всей технике офиса в указанный период")}
              >
                <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="winterFromMonth" label={t("С месяца")}>
                <Select
                  options={MONTHS.map((label, index) => ({ value: index + 1, label: t(label) }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="winterToMonth" label={t("По месяц")}>
                <Select
                  options={MONTHS.map((label, index) => ({ value: index + 1, label: t(label) }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Typography.Text strong>{t('Поля заданий путевого листа')}</Typography.Text>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Form.Item
                name="taskLayout"
                label={t("Раскладка")}
                tooltip={t("По умолчанию раскладка общая для всей организации. Свою задают только офисам, которым она нужна отдельно.")}
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    {
                      value: INHERIT,
                      label: inheritLabel,
                    },
                    ...Object.values(WaybillTaskLayout).map((value) => ({
                      value,
                      label: t(TASK_LAYOUT_LABEL[value] ?? value),
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item noStyle shouldUpdate={(prev, next) => prev.taskLayout !== next.taskLayout}>
                {({ getFieldValue }) =>
                  getFieldValue('taskLayout') === WaybillTaskLayout.ADDRESS ? (
                    <Form.Item
                      name="taskAddressALocations"
                      label={t("«Адрес А» — список локаций")}
                      valuePropName="checked"
                      tooltip={t("Вместо свободного текста — выбор из справочника «Локации заданий»")}
                    >
                      <Switch />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            </Col>
          </Row>

          {editing && (
            <>
              <Typography.Text strong>{t('Безопасность дорог')}</Typography.Text>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Form.Item
                    name="violationLimit"
                    label={t('Лимит нарушений')}
                    tooltip={t('Водитель, набравший столько действующих нарушений за период, не получает путевые листы, пока служба БД не погасит нарушение')}
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} max={50} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="violationPeriodMonths"
                    label={t('За период, месяцев')}
                    tooltip={t('Скользящее окно: учитываются нарушения за последние N месяцев')}
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} max={120} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Typography.Text strong>{t('Нумерация документов')}</Typography.Text>
          <Typography.Paragraph type="secondary" style={{ marginTop: 4 }}>
            {t(
              'Префикс по каждому виду документа, например «ТТН» вместо «PL» — под собственную нумерацию офиса. Пустое поле — значение платформы по умолчанию.',
            )}
          </Typography.Paragraph>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const officeCode = (getFieldValue('code') as string | undefined) || 'JIZ';
              return (
                <Row gutter={[16, 8]}>
                  {Object.values(DocumentKind).map((kind) => {
                    const current = (
                      getFieldValue(['documentNumberPrefixes', kind]) as string | undefined
                    )?.trim();
                    const example = `${current || DEFAULT_DOCUMENT_PREFIX[kind]}-${officeCode}-${new Date().getFullYear()}-000001`;
                    return (
                      <Col span={8} key={kind}>
                        <Form.Item
                          name={['documentNumberPrefixes', kind]}
                          label={t(DOCUMENT_KIND_LABEL[kind])}
                          rules={[
                            {
                              pattern: /^[A-ZА-ЯЁ0-9-]{0,12}$/u,
                              message: t('Заглавные буквы, цифры, дефис, до 12 символов'),
                            },
                          ]}
                        >
                          <Input placeholder={DEFAULT_DOCUMENT_PREFIX[kind]} />
                        </Form.Item>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {example}
                        </Typography.Text>
                      </Col>
                    );
                  })}
                </Row>
              );
            }}
          </Form.Item>

          <Space>
            <Form.Item name="phone" label={t("Телефон")}>
              <Input style={{ width: 220 }} />
            </Form.Item>
            {editing && (
              <Form.Item name="isActive" label={t("Активен")} valuePropName="checked">
                <Switch />
              </Form.Item>
            )}
          </Space>
          <Form.Item name="address" label={t("Адрес")}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <OfficeTerritoryPicker
        open={territoryOpen}
        value={territoryStart}
        onCancel={() => setTerritoryOpen(false)}
        onOk={(territory) => {
          form.setFieldsValue({
            latitude: Number(territory.lat.toFixed(7)),
            longitude: Number(territory.lon.toFixed(7)),
            mapRadiusKm: territory.radiusKm,
          });
          setTerritoryOpen(false);
        }}
      />
    </div>
  );
}
